import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

class FeaturedCollectionAdd extends HTMLElement {
  #abortController = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get variantId() {
    return this.dataset.variantId;
  }

  get cartQuantity() {
    return Number(this.dataset.cartQuantity || 0);
  }

  set cartQuantity(value) {
    const next = Math.max(0, Number(value) || 0);
    this.dataset.cartQuantity = String(next);
    this.dataset.state = next > 0 ? 'stepper' : 'add';

    const display = this.querySelector('[data-quantity-display]');
    if (display) display.textContent = String(next);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!button || this.#busy || this.dataset.unavailable === 'true') return;

    const action = button.getAttribute('data-action');

    if (action === 'add') {
      event.preventDefault();
      this.#addToCart();
    } else if (action === 'increment') {
      event.preventDefault();
      this.#changeQuantity(this.cartQuantity + 1);
    } else if (action === 'decrement') {
      event.preventDefault();
      this.#changeQuantity(this.cartQuantity - 1);
    }
  };

  /**
   * Optimistically updates UI then calls cart API.
   */
  async #addToCart() {
    if (!this.variantId) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    const prevQty = this.cartQuantity;
    this.cartQuantity = prevQty + 1;

    try {
      const sections = this.#collectSectionIds();
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(this.variantId), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || 'Add to cart failed');
      }

      const cart = await this.#fetchCart();
      const newQty = this.#findVariantQuantity(cart);
      this.cartQuantity = newQty;

      this.#dispatchCartEvent(cart, data.sections, newQty - prevQty > 0);
    } catch (error) {
      this.cartQuantity = prevQty;
      console.error('[featured-collection-add] add failed:', error);
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Sets the cart line for this variant to the given quantity (0 removes).
   * @param {number} newQuantity
   */
  async #changeQuantity(newQuantity) {
    if (!this.variantId) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    const prevQty = this.cartQuantity;
    const target = Math.max(0, newQuantity);
    this.cartQuantity = target;

    try {
      const sections = this.#collectSectionIds();
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: target,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart.status) {
        throw new Error(cart.message || 'Cart change failed');
      }

      const newQty = this.#findVariantQuantity(cart);
      this.cartQuantity = newQty;

      this.#dispatchCartEvent(cart, cart.sections, newQty - prevQty > 0);
    } catch (error) {
      this.cartQuantity = prevQty;
      console.error('[featured-collection-add] change failed:', error);
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Sync from external cart changes (cart drawer, other cards, etc).
   * @param {CartUpdateEvent | CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (event.target === this) return;
    if (event.detail?.data?.source === 'featured-collection-add' && event.detail?.sourceId === this.variantId) {
      return;
    }

    const resource = event.detail?.resource;
    if (resource?.items) {
      this.cartQuantity = this.#findVariantQuantity(resource);
      return;
    }

    const cart = await this.#fetchCart();
    if (cart) this.cartQuantity = this.#findVariantQuantity(cart);
  };

  /**
   * @returns {Promise<{items?: Array<{variant_id: number, quantity: number}>} | null>}
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      return await response.json();
    } catch (error) {
      console.error('[featured-collection-add] cart fetch failed:', error);
      return null;
    }
  }

  /**
   * @param {{items?: Array<{variant_id: number, quantity: number}>} | null | undefined} cart
   * @returns {number}
   */
  #findVariantQuantity(cart) {
    if (!cart?.items || !this.variantId) return 0;
    const targetId = String(this.variantId);
    const line = cart.items.find((item) => String(item.variant_id) === targetId);
    return line ? line.quantity : 0;
  }

  /**
   * Collect section IDs that should re-render after cart mutations
   * (cart drawer, cart icon section, cart page).
   * @returns {string[]}
   */
  #collectSectionIds() {
    const ids = new Set();
    for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    }
    return Array.from(ids);
  }

  /**
   * @param {{item_count?: number} & object} cart
   * @param {Record<string, string> | undefined} sections
   * @param {boolean} isAdd - true when this update increased the cart line
   */
  #dispatchCartEvent(cart, sections, isAdd) {
    const eventClass = isAdd ? CartAddEvent : CartUpdateEvent;
    this.dispatchEvent(
      new eventClass(cart, this.variantId, {
        source: 'featured-collection-add',
        itemCount: cart?.item_count ?? 0,
        variantId: this.variantId,
        productId: this.dataset.productId,
        sections,
      })
    );
  }
}

if (!customElements.get('featured-collection-add')) {
  customElements.define('featured-collection-add', FeaturedCollectionAdd);
}
